import type { Programme } from "@/types";

import { isCasProgramme, isFeAwardType } from "./award-types";
import casDevelopmentRules from "./cas-development-rules.generated.json";

export const DEVELOPMENT_SETUP_STEP_KEYS = [
  "identity",
  "outcomes",
  "versions",
  "structure",
] as const;

export type DevelopmentSetupStepKey = (typeof DEVELOPMENT_SETUP_STEP_KEYS)[number] | "cas-picker";

export function getDevelopmentSetupStepKeys(programme: Programme): DevelopmentSetupStepKey[] {
  return isCasProgramme(programme)
    ? ["identity", "cas-picker", "versions", "structure"]
    : [...DEVELOPMENT_SETUP_STEP_KEYS];
}

export interface DevelopmentSetupRequirement {
  key: DevelopmentSetupStepKey;
  label: string;
  complete: boolean;
}

export interface DevelopmentSetupResult {
  complete: boolean;
  completedCount: number;
  requirements: DevelopmentSetupRequirement[];
}

interface CasDevelopmentRule {
  requiredCredits: number;
  mandatoryComponentCodes: string[];
  allowedComponentCodes: string[];
  groups: { minimumCredits: number; componentCodes: string[] }[];
}

const CAS_DEVELOPMENT_RULES = casDevelopmentRules as Record<string, CasDevelopmentRule>;

function casComponentCodeOf(module: Programme["modules"][number]): string {
  // Falls through on an empty string as well as an absent one, so `??` will
  // not do: a module carrying a blank component code is identified by its
  // module code.
  const componentCode = module.casComponentCode?.trim() ?? "";
  return componentCode.length > 0 ? componentCode : (module.code?.trim() ?? "");
}

/** Whether a CAS programme's chosen components satisfy its major award. */
export function isCasSelectionComplete(programme: Programme): boolean {
  const majorAwardCode = programme.casMajorAwardCode?.trim() ?? "";
  const rule = CAS_DEVELOPMENT_RULES[majorAwardCode];
  if (!rule) {
    // A code the rules do not list has nothing to check against. Refusing it
    // held outcomes and every later step shut, and validation already warns.
    return majorAwardCode.length > 0;
  }
  const selected = (programme.modules ?? []).filter((module) => module.casSource === true);
  const selectedCodes = new Set(selected.map(casComponentCodeOf));
  // Summed over the modules rather than a code-keyed map: two modules sharing a
  // component code are still two modules, and folding them would drop the
  // second one's credits out of the total the learner actually carries.
  const creditsIn = (codes: ReadonlySet<string>): number =>
    selected.reduce(
      (total, module) =>
        codes.has(casComponentCodeOf(module)) ? total + Number(module.credits ?? 0) : total,
      0,
    );
  // A handful of QQI major awards publish no component rule items at all. Their
  // rule carries no code list to check against, so the component constraints
  // simply do not apply. Enforcing an empty allow-list would make those awards
  // permanently incompletable.
  const allowedCodes = new Set(rule.allowedComponentCodes);
  const componentRulesApply = allowedCodes.size > 0;
  return (
    selectedCodes.size > 0 &&
    creditsIn(selectedCodes) === rule.requiredCredits &&
    Number(programme.totalCredits) === rule.requiredCredits &&
    (!componentRulesApply ||
      (rule.mandatoryComponentCodes.every((code) => selectedCodes.has(code)) &&
        Array.from(selectedCodes).every((code) => allowedCodes.has(code)) &&
        rule.groups.every(
          (group) => creditsIn(new Set(group.componentCodes)) >= group.minimumCredits,
        )))
  );
}

/** The identity fields every other step needs: title, award type, level, credits. */
export function identitySetupComplete(programme: Programme): boolean {
  const nfqLevel = Number(programme.nfqLevel);
  return (
    Boolean((programme.title ?? "").trim()) &&
    Boolean((programme.awardType ?? "").trim()) &&
    Number.isFinite(nfqLevel) &&
    nfqLevel > 0 &&
    Number(programme.totalCredits) > 0
  );
}

/** What a step waits on: Identity, a CAS programme's components, or all of setup. */
export type StepGate = "identity" | "cas-components" | "setup";

/**
 * When a step opens. Wider than the setup requirements: CAS components are
 * chosen straight after Identity for any FE award, though only a CAS
 * programme (an FE Major Award, or one linked to a CAS award) counts them
 * towards setup, and that programme's outcomes come from the components it
 * chose.
 */
export function stepGate(programme: Programme, stepKey: string): StepGate {
  const setupKeys: readonly string[] = getDevelopmentSetupStepKeys(programme);
  if (setupKeys.includes(stepKey)) {
    return "identity";
  }
  if (stepKey === "cas-picker" && isFeAwardType(programme.awardType ?? "")) {
    return "identity";
  }
  if (stepKey === "outcomes" && isCasProgramme(programme)) {
    return "cas-components";
  }
  return "setup";
}

/** The minimum programme shell required before module development can begin. */
export function evaluateDevelopmentSetup(programme: Programme): DevelopmentSetupResult {
  const baseRequirements: DevelopmentSetupRequirement[] = [
    {
      key: "identity",
      label: "Identity",
      complete: identitySetupComplete(programme),
    },
    {
      key: "outcomes",
      label: "Programme outcomes",
      complete: (programme.plos ?? []).some((plo) => Boolean((plo.text ?? "").trim())),
    },
    {
      key: "versions",
      label: "Programme versions",
      complete: (programme.versions ?? []).length > 0,
    },
    {
      key: "structure",
      label: "Modules and electives",
      complete: (programme.modules ?? []).length > 0,
    },
  ];
  const requirements = getDevelopmentSetupStepKeys(programme).map((key) => {
    if (key !== "cas-picker") {
      return baseRequirements.find((requirement) => requirement.key === key)!;
    }
    return {
      key,
      label: "CAS components",
      complete: isCasSelectionComplete(programme),
    };
  });
  const completedCount = requirements.filter((requirement) => requirement.complete).length;
  return {
    complete: completedCount === requirements.length,
    completedCount,
    requirements,
  };
}
